import { DotsWithPopup, toast } from "@risalabs_frontend_org/oasis-ui-kit";
import React, { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { updateBigQueryData } from "../../../../api/postCall/updateBigQueryData";
import { TableCellDotsWithPopup } from "../../../../data-model/tableCells";
import { logError } from "../../../../utils/customLogger";

interface DotsWithPopupCellProps {
  value: TableCellDotsWithPopup | null | undefined;
  [key: string]: unknown;
}

/** Value written to BigQuery for each selected workflow checklist item. */
const BIGQUERY_COMPLETED_VALUE = "completed";

/**
 * Serializes checkbox defaults for effect dependencies when the parent passes a new object each render.
 *
 * @param byId - Map of checkbox id to checked flag
 * @returns Stable string for dependency comparison
 */
const serializeCheckedById = (
  byId: Record<string, boolean> | undefined,
): string => JSON.stringify(byId ?? {});

/**
 * Builds a full `id -> checked` map for every checklist row so the popup state is complete.
 *
 * @param items - Checkbox definitions with stable `id`s
 * @param defaults - Optional defaults from row data (missing ids treated as unchecked)
 * @returns Map including every `items[].id`
 */
const mergeCheckedByIdWithItems = (
  items: Array<{ id: string }>,
  defaults: Record<string, boolean> | undefined,
): Record<string, boolean> => {
  const next: Record<string, boolean> = {};
  for (const it of items) {
    next[it.id] = Boolean(defaults?.[it.id]);
  }
  return next;
};

/**
 * Builds `update_data` for checked checklist rows only.
 *
 * @param checkedById - Current checkbox selection
 * @param fieldByCheckboxId - Maps checkbox id to BigQuery column / JSON path
 * @returns Payload fragment for `updateBigQueryData`, or null if nothing selected
 */
const buildBigQueryUpdateDataFromCheckedIds = (
  checkedById: Record<string, boolean>,
  fieldByCheckboxId: Record<string, string>,
): Record<string, string> | null => {
  const updateData: Record<string, string> = {};
  for (const [checkboxId, fieldKey] of Object.entries(fieldByCheckboxId)) {
    if (checkedById[checkboxId]) {
      updateData[fieldKey] = BIGQUERY_COMPLETED_VALUE;
    }
  }
  return Object.keys(updateData).length > 0 ? updateData : null;
};

/**
 * Renders Oasis `DotsWithPopup` for a table cell (three dots + anchored popup).
 *
 * @param value - Checklist `items` and optional `checkboxesGroupLabel`; invalid values render an empty cell
 */
export const DotsWithPopupCell: React.FC<DotsWithPopupCellProps> = ({
  value,
}) => {
  const items = value?.items ?? [];
  const itemIdsKey = items.map((it) => it.id).join(",");

  const [checkedById, setCheckedById] = useState<Record<string, boolean>>(() =>
    mergeCheckedByIdWithItems(items, value?.defaultCheckedById),
  );

  const checkedByIdRef = useRef(checkedById);
  checkedByIdRef.current = checkedById;

  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    setCheckedById(mergeCheckedByIdWithItems(items, value?.defaultCheckedById));
    // `itemIdsKey` + serialized defaults capture row/checklist changes without listing `items`/`value` (new refs each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemIdsKey, serializeCheckedById(value?.defaultCheckedById)]);

  if (!value || typeof value !== "object") {
    return (
      <div className="flex min-h-[24px] min-w-[24px] items-center justify-center" />
    );
  }

  const hasLabel = typeof value.checkboxesGroupLabel === "string";
  if (items.length === 0 && !hasLabel) {
    return (
      <div className="flex min-h-[24px] min-w-[24px] items-center justify-center" />
    );
  }

  const handleMarkAsSuccess = async () => {
    const v = valueRef.current;
    const snapshot = { ...checkedByIdRef.current };
    const fieldMap = v?.markSuccessBigQueryFieldByCheckboxId;
    const rowId = v?.rowIdentifier?.trim();

    if (!fieldMap || !rowId) {
      return;
    }

    const updateData = buildBigQueryUpdateDataFromCheckedIds(
      snapshot,
      fieldMap,
    );
    if (!updateData) {
      toast.error("Select at least one item to mark as success.");
      return;
    }

    try {
      await updateBigQueryData({
        filters: { identifier: rowId },
        update_data: updateData,
        request_id: uuidv4(),
      });
      toast.success("Row updated successfully.");
      v?.onMarkSuccess?.();
    } catch (error) {
      logError(error as Error, "DotsWithPopupCell.updateBigQueryData");
      toast.error("Could not update row. Please try again.");
      throw error;
    }
  };

  return (
    <div className="flex items-center justify-center py-1">
      <DotsWithPopup
        closeOnMarkSuccess
        triggerAriaLabel={
          value.triggerAriaLabel ?? "Open row details and actions"
        }
        popup={{
          checkboxesGroupLabel: value.checkboxesGroupLabel ?? "",
          items,
          checkedById,
          onCheckedChange: setCheckedById,
          cancelLabel: "Close",
          markSuccessLabel: value.markSuccessLabel ?? "Mark as success",
          onCancel: () => {},
          onMarkAsSuccess: handleMarkAsSuccess,
        }}
      />
    </div>
  );
};

export default DotsWithPopupCell;
